
import { computed, inject, nextTick, ref } from 'vue';
import { NodeType } from '@amplify/amp-module';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import { useNodeActions } from '@/composables/useNodeActions';
import { isValidDrop, getValidNodesFromDirection } from '@/utils/builder-utils';
import { TOOLBAR_TOOLS } from '@/constants/amp.js';
import { GetChildNodesByParentId, GetDragging } from '@/constants/getters.js';
import { onClickOutside } from '@/directives/onClickOutside';
import { NodeOrder } from '@/constants/builder.js';
import { PasteNodes } from '~/constants/actions';

export default {
  directives: {
    'click-outside': onClickOutside,
  },

  props: {
    action: { type: Object, default: () => {} },
    forceVisible: { type: Boolean, default: false },
    showAction: { type: Boolean, default: false },
    ignoreDropZone: { type: Boolean, default: false },
    nodeType: { type: String, default: '' },
    nodeId: { type: String, default: '' },
    subNodeId: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
  },

  setup(props, { emit }) {
    const { store } = useStoreInNodes();
    const { addingNode, addNodeOfType, onDrop } = useNodeActions();

    const getGraph = inject('getGraph', () => {});

    const showMenu = ref(false);
    const selfHover = ref(false);
    const dropzone = ref(null);
    const menu = ref(null);

    const dragging = computed(() => store.value.getters[`builderStore/${GetDragging}`]);
    const nodesClipboard = computed(() => store.value.state.builderStore.nodesClipboard);

    const canPasteNodes = computed(() => {
      if (!nodesClipboard.value.length) {
        return false;
      }

      const childNodes = store.value.getters[`builderStore/${GetChildNodesByParentId}`]({
        parentId: props.subNodeId ? `${props.nodeId}_port_${props.subNodeId}` : props.nodeId,
      });

      // multi nodes paste is allowed just for the leaf nodes for now
      if (nodesClipboard.value.length > 1 && childNodes.length) {
        return false;
      }

      const parentNode = nodesClipboard.value[0];
      const isGroupNode = [NodeType.buttonGroup, NodeType.question].includes(parentNode.type);

      // pasting a group node with more than one sub node inside flow is not allowed for now
      if (childNodes.length && isGroupNode && Object.keys(parentNode.subNodes || {}).length > 1) {
        return false;
      }

      // nodeType contains the node types that can be added as a child to the nodeId
      if (!nodeTypes.value.find((nodeType) => nodeType.value === parentNode.type)) {
        return false;
      }

      return true;
    });

    const dropzoneClasses = computed(() => ({
      'drop-zone': dragging.value && !props.ignoreDropZone && isDropValid.value,
      hovered: selfHover.value,
    }));

    const actionButtonClasses = computed(() => [
      'action-button',
      {
        'side-button': props.action.key !== 'custom',
        clickable: !dragging.value && (!addingNode.value || isDropValid.value),
        focused: showMenu.value,
      },
    ]);

    const outgoingEdges = computed(() =>
      props.subNodeId
        ? getGraph()
            .getOutgoingEdges(props.nodeId)
            ?.filter((edge) => edge.getSourcePortId()?.endsWith(props.subNodeId)) || null
        : getGraph().getOutgoingEdges(props.nodeId)
    );

    const targetHasChildren = computed(() => !!(outgoingEdges.value !== null && outgoingEdges.value.length));

    const nodeTypes = computed(() => {
      const isTop = props.action.key === 'top';

      return getValidNodesFromDirection(props.nodeType, isTop, targetHasChildren.value)
        .reduce((acc, val) => {
          const tool = TOOLBAR_TOOLS.find((tool) => tool.name === val);

          if (tool) {
            acc[NodeOrder.indexOf(val)] = {
              // Adds properly formatted node type objects at ordered indexes
              value: tool.name,
              text: tool.text,
              icon: tool.icon,
            };
          }

          return acc;
        }, [])
        .filter((v) => v); // Removes empty array indexes;
    });

    const isDropValid = computed(() =>
      isValidDrop(props.nodeType, addingNode.value?.type, targetHasChildren.value, props.action.key === 'top')
    );

    const visible = computed(() => {
      if (props.forceVisible) {
        return true;
      }

      if (addingNode.value === null) {
        return props.showAction || showMenu.value;
      }

      return ['right', 'left'].includes(props.action.key)
        ? props.nodeType === addingNode.value.type
        : isDropValid.value;
    });

    const onClose = () => (showMenu.value = false);

    const setDropEffect = (e, selfHovered) => {
      if (!isDropValid.value || props.readonly) {
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      selfHover.value = selfHovered;
    };

    const onActionClick = () => {
      if (props.readonly) {
        return;
      }

      if (props.action.handler) {
        props.action.handler();
        return;
      }

      if (!props.action.click) {
        console.error('No Action Specified for Node Action Click!');
        return;
      }

      if (addingNode.value?.type) {
        if (!isDropValid.value) {
          return;
        }

        addNodeOfType(props.nodeId, { ...addingNode.value, side: props.action.key }, props.subNodeId);
        return;
      }

      displayMenu();
    };

    const onNodeDrop = (e) => {
      if (!isDropValid.value || props.readonly) {
        return;
      }

      if (props.action.handler) {
        props.action.handler();
        return;
      }

      e.preventDefault();
      onDrop(e, props.nodeId, props.subNodeId, props.action.key);
    };

    const onMenuClick = (type) => {
      // Type can vary here, so we can't just use computed property
      if (isValidDrop(props.nodeType, type, targetHasChildren.value, props.action.key === 'top')) {
        addNodeOfType(props.nodeId, { type, side: props.action.key }, props.subNodeId);
      }
    };

    const displayMenu = async () => {
      showMenu.value = true;
      getGraph().getCellById(props.nodeId)?.toFront();

      if (props.action.key !== 'custom') {
        await nextTick();

        const yStyleAttribute = (key) => {
          switch (key) {
            case 'top':
              return 'bottom';

            case 'bottom':
              return 'top';

            default:
              return key;
          }
        };

        menu.value.style[yStyleAttribute(props.action.key)] = '30px';
        menu.value.style.left = `${(dropzone.value.clientWidth - menu.value.clientWidth) / 2}px`;
      }
    };

    const onClickOutsideMenu = () => (showMenu.value = false);

    const onPaste = () => {
      store.value.dispatch(`builderStore/${PasteNodes}`, {
        nodeId: props.nodeId,
        subNodeId: props.subNodeId,
        side: props.action.key,
      });
      showMenu.value = false;
    };

    return {
      actionButtonClasses,
      canPasteNodes,
      dropzoneClasses,
      dropzone,
      isDropValid,
      menu,
      nodesClipboard,
      nodeTypes,
      selfHover,
      showMenu,
      visible,
      onActionClick,
      onNodeDrop,
      onMenuClick,
      displayMenu,
      onClose,
      onClickOutsideMenu,
      onPaste,
      setDropEffect,
    };
  },
};
